import { useCallback, useEffect, useState } from "react";

export interface UseStepConfigPanelOptions<T> {
    transformDraft?: (prev: T, partial: Partial<T>) => T;
}

export interface UseStepConfigPanelResult<T> {
    draftData: T;
    updateDraft: (partial: Partial<T>) => void;
    commitDraft: (override?: Partial<T>) => void;
    hasDraftChanges: boolean;
    setInitialData: (data: T) => void;
}

export function useStepConfigPanel<T extends object>(
    nodeId: string,
    persistedData: T,
    updateStepData: (stepId: string, data: Partial<T>) => void,
    options?: UseStepConfigPanelOptions<T>
): UseStepConfigPanelResult<T> {
    
    const [initialData, setInitialData] = useState<T>(persistedData);
    const [draftData, setDraftData] = useState<T>(persistedData);
    const transformDraft = options?.transformDraft;

    useEffect(() => {
        setInitialData(persistedData);
        setDraftData(persistedData);
    }, [nodeId, persistedData]);

    const updateDraft = useCallback(
        (partial: Partial<T>) => {
            if (transformDraft) {
                setDraftData((prev) => transformDraft(prev, partial));
            } else {
                setDraftData((prev) => ({ ...prev, ...partial }));
            }
        },
        [transformDraft]
    );

    const commitDraft = useCallback(
        (override?: Partial<T>) => {
            if (!nodeId) return;
            const toCommit = override ? ({ ...draftData, ...override } as T) : draftData;
            updateStepData(nodeId, toCommit);
            setInitialData(toCommit);
            setDraftData(toCommit);
        },
        [nodeId, draftData, updateStepData]
    );

    const hasDraftChanges = JSON.stringify(initialData) !== JSON.stringify(draftData);

    return {
        draftData,
        updateDraft,
        commitDraft,
        hasDraftChanges,
        setInitialData,
    };
}
